import { Component, ReactNode } from 'react';
import { Sentry, isSentryEnabled } from '@/lib/sentry';
import { log } from '@/lib/logger';
import { ErrorDisplay } from '@/components/ErrorDisplay';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: { componentStack: string }) {
    log.error('ERROR_BOUNDARY', 'Caught an error:', error, errorInfo);
    // Report to Sentry with component stack for debugging
    if (isSentryEnabled) {
      Sentry.withScope((scope) => {
        scope.setTag("error.boundary", "true");
        scope.setExtra("componentStack", errorInfo.componentStack);
        Sentry.captureException(error);
      });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorDisplay
          error={this.state.error}
          onReset={() => this.setState({ hasError: false, error: null })}
        />
      );
    }

    return this.props.children;
  }
}

